import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { filter } from 'rxjs/operators';
import { Overlay } from '@angular/cdk/overlay';
import { MatDialog as MatDialog, MatDialogRef as MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Modal2Service } from 'account-hybrid/common/services/modal2.service';

@UntilDestroy()
@Component({
    selector: 'ft-modal2',
    templateUrl: './modal2.component.html',
    styleUrls: ['./modal2.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class Modal2Component implements OnInit, OnDestroy {
    _trigger: HTMLElement;
    triggerListenerFunc: EventListenerOrEventListenerObject;
    @ViewChild('modalTmpl', { static: false }) modalTmpl: TemplateRef<any>;
    @Input() public modalId = (Date.now() + Math.random() * 100).toString(10);
    @Input() title: string;
    @Input() summary: string;
    @Input() theme: 'dark' | 'light' | 'gray' = 'dark';
    /** Optional backdrop class override; defaults to the theme backdrop ('-dark'/'-light'/'-gray'). */
    @Input() backdropClass?: string | string[];
    /**
     * Set false for translucent-backdrop modals: the default block strategy pins <html>
     * at a negative top (cdk-global-scrollblock), and this app's overflow:hidden
     * override clips the whole page away — invisible behind opaque backdrops, a blank
     * void behind see-through ones.
     */
    @Input() blockScroll = true;
    @Input() maxWidth;
    @Input() restoreFocus: boolean = true;
    @Input() closeButtonVisible = true;
    @Input() disableEnterAnimation = false;

    @Input() set trigger(trigger: HTMLElement) {
        this._trigger = trigger;

        if (!this.triggerListenerFunc) {
            this.triggerListenerFunc = this.onTriggerClick.bind(this);
        }

        if (trigger instanceof HTMLElement) {
            trigger.addEventListener('click', this.triggerListenerFunc);
        }
    }

    get trigger(): HTMLElement {
        return this._trigger;
    }

    dialogRef: MatDialogRef<any>;

    isOpened = false;

    @Output() closed = new EventEmitter();
    @Output() done = new EventEmitter<any>();
    @Output() opened = new EventEmitter();

    constructor(private dialog: MatDialog, private modalService: Modal2Service, private overlay: Overlay) {
    }

    ngOnInit() {
        this.modalService.openModalByIdSubject
            .pipe(
                untilDestroyed(this)
            )
            .subscribe(id => {
                if (id === this.modalId) {
                    this.open();
                }
            });
    }

    ngOnDestroy() {
        if (this.triggerListenerFunc) {
            this.trigger?.removeEventListener(
                'click',
                this.triggerListenerFunc
            );
            this.triggerListenerFunc = null;
        }
    }

    onTriggerClick(event: MouseEvent) {
        event.stopPropagation();

        this.open();
    }

    open() {
        this.isOpened = true;
        this.dialogRef = this.dialog.open(this.modalTmpl, {
            panelClass: 'ft2',
            id: this.modalId,
            backdropClass: this.backdropClass ?? `-${this.theme}`,
            ...(this.blockScroll ? {} : { scrollStrategy: this.overlay.scrollStrategies.noop() }),
            data: this,
            autoFocus: 'dialog',
            restoreFocus: this.restoreFocus,
            ...(this.disableEnterAnimation ? {
                enterAnimationDuration: '0ms', // TODO string is deprecated start from 17.0.0
            } : {}),
        });
        this.dialogRef.afterOpened().subscribe(() => {
            this.opened.emit();
        });
        this.dialogRef.afterClosed().subscribe(() => {
            this.closed.emit();
        });
        this.dialogRef
            .keydownEvents()
            .pipe(filter((event) => this.isOpened && event.key === 'Escape'))
            .subscribe(() => {
                this.close();
            });
    }

    close() {
        this.isOpened = false;
        this.dialogRef?.close();
        this.closed.emit();
    }

    onDone() {
        this.close();
        this.done.emit();
    }
}
